'use client';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { MagnifyingGlassIcon, XMarkIcon, FunnelIcon, ChevronDownIcon, PencilSquareIcon, TrashIcon, CameraIcon, ArrowPathIcon, MapPinIcon, ExclamationTriangleIcon, CheckCircleIcon, SunIcon, MoonIcon, PlayIcon, PauseIcon, MapIcon, BellAlertIcon, ShieldExclamationIcon,  } from '@heroicons/react/24/outline';
import { INITIAL_DOTS, DotPerson, CameraStatus, DotColor, getEffectiveDotColor, formatRelativeTime, formatTimestamp, getCameraLabel, getColorLabel, generateDotId, TRAJECTORY_ROUTES, ZONE_POLYGONS } from '@/data/dotsData';
import StatusBadge from '@/components/ui/StatusBadge';
import CameraBadge from '@/components/ui/CameraBadge';
import CameraPanel from './CameraPanel';
import EnviarAlertaPanel from './EnviarAlertaPanel';
import { useGrid } from '@/context/GridContext';
import { useAlerts } from '@/context/AlertContext';

// Dynamic import for map (no SSR)
const MapView = dynamic(() => import('./MapView'), { ssr: false, loading: () => <MapSkeleton /> });

function MapSkeleton() {
  return (
    <div className="w-full h-full bg-muted animate-pulse flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <MapPinIcon className="w-8 h-8 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Cargando mapa…</span>
      </div>
    </div>
  );
}

type FilterColor = 'all' | DotColor;
type FilterCamera = 'all' | CameraStatus;

export default function InteractiveMapScreen() {
  const [dots, setDots] = useState<DotPerson[]>(INITIAL_DOTS);
  const [selectedDot, setSelectedDot] = useState<DotPerson | null>(null);
  const [selectedDotIds, setSelectedDotIds] = useState<Set<string>>(new Set());
  const [contextMenu, setContextMenu] = useState<{ dot: DotPerson; x: number; y: number } | null>(null);
  const [multiContextMenu, setMultiContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [filterColor, setFilterColor] = useState<Set<DotColor>>(new Set());
  const [filterCamera, setFilterCamera] = useState<FilterCamera>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDot, setEditingDot] = useState<DotPerson | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingDot, setDeletingDot] = useState<DotPerson | null>(null);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [isAddingDot, setIsAddingDot] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [showCameraPanel, setShowCameraPanel] = useState(false);
  const { addDotToGrid, gridDotIds, gridCells, removeDotFromGrid } = useGrid();
  const { dotAlerts, acknowledgeAlert } = useAlerts();

  // Alert panel state
  const [showAlertPanel, setShowAlertPanel] = useState(false);
  const [alertPanelKind, setAlertPanelKind] = useState<'notification' | 'area'>('notification');
  const [alertAreaShape, setAlertAreaShape] = useState<'polygon' | 'circle'>('polygon');
  const [isDrawingArea, setIsDrawingArea] = useState(false);
  const [drawnAreaDotIds, setDrawnAreaDotIds] = useState<Set<string>>(new Set());
  const [showDrawToolbar, setShowDrawToolbar] = useState(false);
  const [drawnPolygon, setDrawnPolygon] = useState<[number, number][] | null>(null);

  // Zone visibility — all zones visible by default
  const [visibleZoneIds, setVisibleZoneIds] = useState<Set<string>>(
    () => new Set(ZONE_POLYGONS.map(z => z.id))
  );
  const [showZoneFilter, setShowZoneFilter] = useState(false);

  // Alert polygons visibility — multi-select filter
  const [showAlertFilter, setShowAlertFilter] = useState(false);
  const [visibleAlertIds, setVisibleAlertIds] = useState<Set<string>>(
    () => new Set(['alert-poly-1', 'alert-poly-2', 'alert-poly-3'])
  );

  const ALERT_FILTER_OPTIONS = [
    { id: 'alert-poly-1', name: 'Alerta Amber', color: '#f59e0b' },
    { id: 'alert-poly-2', name: 'Clave 10-1', color: '#dc2626' },
    { id: 'alert-poly-3', name: 'Clave 61', color: '#06b6d4' },
  ];

  const allAlertsVisible = visibleAlertIds.size === ALERT_FILTER_OPTIONS.length;
  const noAlertsVisible = visibleAlertIds.size === 0;

  const toggleAlertPolygon = useCallback((id: string) => {
    setVisibleAlertIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleZone = useCallback((zoneId: string) => {
    setVisibleZoneIds(prev => {
      const next = new Set(prev);
      if (next.has(zoneId)) next.delete(zoneId);
      else next.add(zoneId);
      return next;
    });
  }, []);

  const allZonesVisible = visibleZoneIds.size === ZONE_POLYGONS.length;

  // Map style and trajectory
  const [mapStyle, setMapStyle] = useState<'dark' | 'light'>('dark');
  const [showTrajectory, setShowTrajectory] = useState(false);
  const [timelineProgress, setTimelineProgress] = useState(100);
  const [isPlaying, setIsPlaying] = useState(false);
  const playIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const timelineRange = (() => {
    let minMs = Infinity;
    let maxMs = -Infinity;
    TRAJECTORY_ROUTES.forEach(r => {
      r.points.forEach(p => {
        const ms = new Date(p.timestamp).getTime();
        if (ms < minMs) minMs = ms;
        if (ms > maxMs) maxMs = ms;
      });
    });
    if (!isFinite(minMs)) { minMs = Date.now() - 60 * 60 * 1000; maxMs = Date.now(); }
    return { minMs, maxMs };
  })();

  const timelineMs = timelineRange.minMs + (timelineProgress / 100) * (timelineRange.maxMs - timelineRange.minMs);

  useEffect(() => {
    if (isPlaying) {
      playIntervalRef.current = setInterval(() => {
        setTimelineProgress(prev => {
          if (prev >= 100) { setIsPlaying(false); return 100; }
          return Math.min(100, prev + 0.5);
        });
      }, 80);
    } else {
      if (playIntervalRef.current) clearInterval(playIntervalRef.current);
    }
    return () => { if (playIntervalRef.current) clearInterval(playIntervalRef.current); };
  }, [isPlaying]);

  useEffect(() => {
    if (!showTrajectory) { setTimelineProgress(100); setIsPlaying(false); }
  }, [showTrajectory]);

  // Add form state
  const [addName, setAddName] = useState('');
  const [addLat, setAddLat] = useState('19.0414');
  const [addLng, setAddLng] = useState('-98.2063');
  const [addCameras, setAddCameras] = useState<CameraStatus>('none');
  const [addError, setAddError] = useState('');

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editCameras, setEditCameras] = useState<CameraStatus>('none');
  const [editError, setEditError] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const multiContextMenuRef = useRef<HTMLDivElement>(null);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const zoneFilterRef = useRef<HTMLDivElement>(null);
  const alertFilterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setInterval(() => {}, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handleGlobalMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const inSingle = contextMenuRef.current?.contains(target);
      const inMulti = multiContextMenuRef.current?.contains(target);
      if (!inSingle && !inMulti) {
        setContextMenu(null);
        setMultiContextMenu(null);
      }
      if (!filterPanelRef.current?.contains(target)) {
        setShowFilterPanel(false);
      }
      if (!zoneFilterRef.current?.contains(target)) {
        setShowZoneFilter(false);
      }
      if (!alertFilterRef.current?.contains(target)) {
        setShowAlertFilter(false);
      }
    };
    document.addEventListener('mousedown', handleGlobalMouseDown);
    return () => document.removeEventListener('mousedown', handleGlobalMouseDown);
  }, []);

  const showToast = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const filteredDots = dots.filter((d) => {
    const color = getEffectiveDotColor(d);
    if (filterColor.size > 0 && !filterColor.has(color as DotColor)) return false;
    if (filterCamera !== 'all' && d.cameras !== filterCamera) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      if (!d.name.toLowerCase().includes(q) && !d.dotId.includes(q)) return false;
    }
    return true;
  });

  const colorCounts = {
    alarma:    dots.filter(d => getEffectiveDotColor(d) === 'alarma').length,
    blue:      dots.filter(d => getEffectiveDotColor(d) === 'blue').length,
    yellow:    dots.filter(d => getEffectiveDotColor(d) === 'yellow').length,
    red:       dots.filter(d => getEffectiveDotColor(d) === 'red').length,
    streaming: dots.filter(d => getEffectiveDotColor(d) === 'streaming').length,
  };

  // Dot detail popup (click popup)
  const [dotPopup, setDotPopup] = useState<{ dot: DotPerson; x: number; y: number } | null>(null);

  const handlePopupAddToGrid = useCallback((dot: DotPerson, type: 'A' | 'B') => {
    addDotToGrid(dot, type);
    showToast(`Cámara ${type} de #${dot.dotId} agregada al grid`);
  }, [addDotToGrid, showToast]);

  const handlePopupSendAlert = useCallback((dot: DotPerson) => {
    setDotPopup(null);
    setSelectedDotIds(new Set([dot.id]));
    setSelectedDot(null);
    setShowAlertPanel(true);
    setAlertPanelKind('notification');
  }, []);

  const handlePopupShowTrajectory = useCallback(() => {
    setShowTrajectory(true);
    setDotPopup(null);
  }, []);

  const handleDotClick = useCallback((dot: DotPerson, isMultiSelect: boolean, isCtrlDeselect: boolean, x?: number, y?: number) => {
    setContextMenu(null);
    setMultiContextMenu(null);
    if (isCtrlDeselect) {
      setSelectedDotIds(prev => { const next = new Set(prev); next.delete(dot.id); return next; });
      return;
    }
    if (isMultiSelect) {
      setSelectedDotIds(prev => {
        const next = new Set(prev);
        if (next.has(dot.id)) next.delete(dot.id);
        else next.add(dot.id);
        return next;
      });
      setSelectedDot(null);
    } else {
      setSelectedDotIds(new Set());
      setSelectedDot(dot);
      // Show click popup
      if (x !== undefined && y !== undefined) {
        setDotPopup({ dot, x, y });
      }
    }
  }, []);

  const handleDotRightClick = useCallback((dot: DotPerson, x: number, y: number) => {
    setMultiContextMenu(null);
    setContextMenu(null);
    setSelectedDotIds(prev => {
      if (prev.size > 1 && prev.has(dot.id)) { setMultiContextMenu({ x, y }); return prev; }
      if (prev.size === 1 && prev.has(dot.id)) { setContextMenu({ dot, x, y }); return prev; }
      setContextMenu({ dot, x, y });
      return new Set();
    });
    setSelectedDot(null);
  }, []);

  const handleMultiRightClick = useCallback((x: number, y: number) => {
    setContextMenu(null);
    setMultiContextMenu({ x, y });
  }, []);

  const handleSelectionChange = useCallback((ids: Set<string>) => {
    setSelectedDotIds(ids);
    setSelectedDot(null);
    setDotPopup(null);
  }, []);

  const handleMapClick = useCallback((lat: number, lng: number) => {
    setDotPopup(null);
    if (isAddingDot) {
      setAddLat(lat.toFixed(5));
      setAddLng(lng.toFixed(5));
      setShowAddModal(true);
      setIsAddingDot(false);
    }
  }, [isAddingDot]);

  // When "Añadir del mapa" is clicked in alert panel — close panel, keep selection mode
  const handleAddFromMapForAlert = useCallback(() => {
    setShowAlertPanel(false);
    showToast('Selecciona puntos en el mapa, luego vuelve a abrir Enviar Alerta', 'success');
  }, [showToast]);

  // Open Enviar Alerta panel (notification mode)
  const handleOpenAlertPanel = useCallback(() => {
    setAlertPanelKind('notification');
    setShowAlertPanel(true);
    setShowCameraPanel(false);
  }, []);

  // Crear Alerta: draw area on map
  const handleCrearAlerta = useCallback(() => {
    setShowDrawToolbar(p => !p);
    setShowAlertPanel(false);
  }, []);

  // When area drawing completes (from MapView callback)
  const handleAreaDrawn = useCallback((dotIds: Set<string>, shape: 'polygon' | 'circle', polygonPoints: [number, number][]) => {
    setDrawnAreaDotIds(dotIds);
    setAlertAreaShape(shape);
    setAlertPanelKind('area');
    setSelectedDotIds(dotIds);
    setShowAlertPanel(true);
    setIsDrawingArea(false);
    setShowDrawToolbar(false);
    if (shape === 'polygon' && polygonPoints.length >= 3) {
      setDrawnPolygon(polygonPoints);
    } else {
      setDrawnPolygon(null);
    }
    showToast(`${dotIds.size} punto${dotIds.size !== 1 ? 's' : ''} en el área seleccionada`);
  }, [showToast]);

  const handleAssignCamera = useCallback((dot: DotPerson, type: 'A' | 'B' | 'both') => {
    setDots(prev => prev.map(d => {
      if (d.id !== dot.id) return d;
      let cameras: CameraStatus = type === 'both' ? 'both' : type;
      if (type === 'A' && d.cameras === 'B') cameras = 'both';
      if (type === 'B' && d.cameras === 'A') cameras = 'both';
      const updated = { ...d, cameras };
      if (selectedDot?.id === d.id) setSelectedDot(updated);
      return updated;
    }));
    if (type === 'both') { addDotToGrid(dot, 'A'); addDotToGrid(dot, 'B'); }
    else addDotToGrid(dot, type);
    setContextMenu(null);
    showToast(`Cámara ${type === 'both' ? 'A + B' : type} asignada a #${dot.dotId}`);
  }, [selectedDot, addDotToGrid, showToast]);

  const handleAssignCameraMulti = useCallback((type: 'A' | 'B' | 'both') => {
    const count = selectedDotIds.size;
    const selectedDots = dots.filter(d => selectedDotIds.has(d.id));
    setDots(prev => prev.map(d => {
      if (!selectedDotIds.has(d.id)) return d;
      let cameras: CameraStatus = type === 'both' ? 'both' : type;
      if (type === 'A' && d.cameras === 'B') cameras = 'both';
      if (type === 'B' && d.cameras === 'A') cameras = 'both';
      return { ...d, cameras };
    }));
    selectedDots.forEach(d => {
      if (type === 'both') { addDotToGrid(d, 'A'); addDotToGrid(d, 'B'); }
      else addDotToGrid(d, type);
    });
    setMultiContextMenu(null);
    setSelectedDotIds(new Set());
    showToast(`Cámara ${type === 'both' ? 'A + B' : type} asignada a ${count} punto${count !== 1 ? 's' : ''}`);
  }, [selectedDotIds, dots, addDotToGrid, showToast]);

  const handleAddDot = useCallback(() => {
    if (!addName.trim()) { setAddError('El nombre es requerido'); return; }
    const latNum = parseFloat(addLat);
    const lngNum = parseFloat(addLng);
    if (isNaN(latNum) || isNaN(lngNum)) { setAddError('Coordenadas inválidas'); return; }
    const newDotId = generateDotId(dots.map(d => d.dotId));
    const newDot: DotPerson = {
      id: `dot-${newDotId}`,
      dotId: newDotId,
      name: addName.trim(),
      lat: latNum,
      lng: lngNum,
      cameras: addCameras,
      lastUpdate: new Date().toISOString(),
    };
    setDots(prev => [...prev, newDot]);
    setShowAddModal(false);
    setAddName(''); setAddLat('19.0414'); setAddLng('-98.2063'); setAddCameras('none'); setAddError('');
    showToast(`Punto #${newDotId} añadido — ${addName.trim()}`);
  }, [addName, addLat, addLng, addCameras, dots, showToast]);

  const handleEditOpen = useCallback((dot: DotPerson) => {
    setEditingDot(dot);
    setEditName(dot.name);
    setEditCameras(dot.cameras);
    setEditError('');
    setContextMenu(null);
    setShowEditModal(true);
  }, []);

  const handleEditSave = useCallback(() => {
    if (!editingDot) return;
    if (!editName.trim()) { setEditError('El nombre es requerido'); return; }
    setDots(prev => prev.map(d => {
      if (d.id !== editingDot.id) return d;
      const updated = { ...d, name: editName.trim(), cameras: editCameras, lastUpdate: new Date().toISOString() };
      if (selectedDot?.id === d.id) setSelectedDot(updated);
      return updated;
    }));
    setShowEditModal(false);
    setEditingDot(null);
    showToast(`Punto #${editingDot.dotId} actualizado`);
  }, [editingDot, editName, editCameras, selectedDot, showToast]);

  const handleDeleteOpen = useCallback((dot: DotPerson) => {
    setDeletingDot(dot);
    setContextMenu(null);
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!deletingDot) return;
    setDots(prev => prev.filter(d => d.id !== deletingDot.id));
    if (selectedDot?.id === deletingDot.id) setSelectedDot(null);
    setShowDeleteConfirm(false);
    showToast(`Punto #${deletingDot.dotId} eliminado`, 'error');
    setDeletingDot(null);
  }, [deletingDot, selectedDot, showToast]);

  const handleRefreshDot = useCallback((dot: DotPerson) => {
    setDots(prev => prev.map(d => {
      if (d.id !== dot.id) return d;
      const updated = { ...d, lastUpdate: new Date().toISOString() };
      if (selectedDot?.id === d.id) setSelectedDot(updated);
      return updated;
    }));
    showToast(`#${dot.dotId} actualizado a ahora`);
  }, [selectedDot, showToast]);

  const handleAssignDotToSlot = useCallback((dotId: string, slotIndex: number, cameraType: 'A' | 'B') => {
    const dot = dots.find(d => d.dotId === dotId);
    if (!dot) return;
    setDots(prev => prev.map(d => {
      if (d.dotId !== dotId) return d;
      let cameras: CameraStatus = cameraType;
      if (cameraType === 'A' && d.cameras === 'B') cameras = 'both';
      if (cameraType === 'B' && d.cameras === 'A') cameras = 'both';
      const updated = { ...d, cameras, lastUpdate: new Date().toISOString() };
      if (selectedDot?.id === d.id) setSelectedDot(updated);
      return updated;
    }));
    showToast(`Punto #${dotId} asignado a Cámara ${cameraType} (slot ${slotIndex + 1})`);
  }, [dots, selectedDot, showToast]);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
    setMultiContextMenu(null);
  }, []);
  const closeDetailPanel = useCallback(() => setSelectedDot(null), []);

  const liveSelectedDot = selectedDot ? dots.find(d => d.id === selectedDot.id) ?? selectedDot : null;

  return (
    <div ref={containerRef} className="relative w-full h-full flex flex-col overflow-hidden">
      {/* Top toolbar */}
      <div className="relative z-[1001] flex items-center gap-2 p-3 bg-background/80 backdrop-blur-sm border-b border-border flex-shrink-0 flex-wrap">
        {/* Search bar */}
        <div className="flex items-center gap-2 bg-card/95 backdrop-blur-sm border border-border rounded-lg px-3 py-2 shadow-lg w-64">
          <MagnifyingGlassIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <input
            type="text"
            placeholder="Buscar por nombre o ID…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none w-full"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="text-muted-foreground hover:text-foreground transition-colors">
              <XMarkIcon className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Filter button */}
        <div className="relative" ref={filterPanelRef}>
          <button
            onClick={(e) => { e.stopPropagation(); setShowFilterPanel(p => !p); }}
            className={`flex items-center gap-2 bg-card/95 backdrop-blur-sm border rounded-lg px-3 py-2 text-sm font-medium shadow-lg transition-all duration-150 ${
              filterColor.size > 0 || filterCamera !== 'all' ? 'border-primary text-primary' : 'border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            <FunnelIcon className="w-3.5 h-3.5" />
            Filtrar
            {(filterColor.size > 0 || filterCamera !== 'all') && (
              <span className="bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold">
                {filterColor.size + (filterCamera !== 'all' ? 1 : 0)}
              </span>
            )}
            <ChevronDownIcon className="w-3 h-3" />
          </button>

          {showFilterPanel && (
            <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-xl p-3 w-56 z-50" onClick={e => e.stopPropagation()}>
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Estado</p>
              <div className="flex flex-col gap-1 mb-3">
                {(['all', 'alarma', 'streaming', 'blue', 'yellow', 'red'] as const).map(c => {
                  const isActive = c === 'all' ? filterColor.size === 0 : filterColor.has(c as DotColor);
                  return (
                    <button key={`fc-${c}`} onClick={() => {
                      if (c === 'all') {
                        setFilterColor(new Set());
                        setShowFilterPanel(false);
                      } else {
                        setFilterColor(prev => {
                          const next = new Set(prev);
                          next.has(c as DotColor) ? next.delete(c as DotColor) : next.add(c as DotColor);
                          return next;
                        });
                      }
                    }}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm font-medium transition-all duration-150 ${isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}>
                      {c !== 'all' && <span className={`w-2 h-2 rounded-full shrink-0 ${c === 'blue' ? 'bg-blue-400' : c === 'yellow' ? 'bg-yellow-400' : c === 'streaming' ? 'bg-green-400' : c === 'alarma' ? 'bg-red-400' : 'bg-black'} ${isActive ? 'ring-2 ring-white/50' : ''}`} />}
                      <span className="flex-1 text-left">{c === 'all' ? 'Todos los estados' : getColorLabel(c)}</span>
                      {isActive && <span className="text-xs">✓</span>}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Cámara</p>
              <div className="flex flex-col gap-1">
                {(['all', 'none', 'A', 'B', 'both'] as const).map(c => {
                  const isActive = filterCamera === c;
                  return (
                    <button key={`fca-${c}`} onClick={() => { setFilterCamera(c); setShowFilterPanel(false); }}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm font-medium transition-all duration-150 ${isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}>
                      <span className="flex-1 text-left">{c === 'all' ? 'Todas las cámaras' : getCameraLabel(c)}</span>
                      {isActive && <span className="text-xs">✓</span>}
                    </button>
                  );
                })}
              </div>
              {(filterColor.size > 0 || filterCamera !== 'all') && (
                <button onClick={() => { setFilterColor(new Set()); setFilterCamera('all'); setShowFilterPanel(false); }}
                  className="mt-2 w-full text-xs text-muted-foreground hover:text-foreground border border-border rounded-md py-1.5 transition-colors">
                  Limpiar filtros
                </button>
              )}
            </div>
          )}
        </div>

        {/* Zone filter button */}
        <div className="relative" ref={zoneFilterRef}>
          <button
            onClick={(e) => { e.stopPropagation(); setShowZoneFilter(p => !p); if (showFilterPanel) setShowFilterPanel(false); if (showAlertFilter) setShowAlertFilter(false); }}
            className={`flex items-center gap-2 bg-card/95 backdrop-blur-sm border rounded-lg px-3 py-2 text-sm font-medium shadow-lg transition-all duration-150 ${
              !allZonesVisible ? 'border-primary text-primary' : 'border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            <MapIcon className="w-3.5 h-3.5" />
            Zonas
            {!allZonesVisible && (
              <span className="bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold">
                {visibleZoneIds.size}
              </span>
            )}
            <ChevronDownIcon className="w-3 h-3" />
          </button>

          {showZoneFilter && (
            <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-xl p-3 w-56 z-50" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Zonas geográficas</p>
                <button onClick={() => { if (allZonesVisible) setVisibleZoneIds(new Set()); else setVisibleZoneIds(new Set(ZONE_POLYGONS.map(z => z.id))); }}
                  className="text-[10px] text-primary hover:underline">
                  {allZonesVisible ? 'Ocultar todas' : 'Mostrar todas'}
                </button>
              </div>
              <div className="flex flex-col gap-1">
                {ZONE_POLYGONS.map(zone => {
                  const isVisible = visibleZoneIds.has(zone.id);
                  return (
                    <button key={zone.id} onClick={() => toggleZone(zone.id)}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-all duration-150 ${isVisible ? 'bg-muted/40 text-foreground' : 'text-muted-foreground hover:bg-muted/20 hover:text-foreground'}`}>
                      <span className="w-3 h-3 rounded-sm shrink-0 border" style={{ background: isVisible ? zone.color + '55' : 'transparent', borderColor: zone.color }} />
                      <span className="flex-1 text-left text-xs">{zone.name}</span>
                      <span className={`w-2 h-2 rounded-full shrink-0 transition-opacity ${isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ background: zone.color }} />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Alert polygons filter dropdown */}
        <div className="relative" ref={alertFilterRef}>
          <button
            onClick={(e) => { e.stopPropagation(); setShowAlertFilter(p => !p); }}
            className={`flex items-center gap-2 bg-card/95 backdrop-blur-sm border rounded-lg px-3 py-2 text-sm font-medium shadow-lg transition-all duration-150 ${
              visibleAlertIds.size > 0 ? 'border-orange-500/60 text-orange-400' : 'border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${visibleAlertIds.size > 0 ? 'bg-orange-400 animate-pulse' : 'bg-muted-foreground'}`} />
            Alertas activas
            {visibleAlertIds.size > 0 && visibleAlertIds.size < ALERT_FILTER_OPTIONS.length && (
              <span className="bg-orange-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold">
                {visibleAlertIds.size}
              </span>
            )}
            <ChevronDownIcon className="w-3 h-3" />
          </button>

          {showAlertFilter && (
            <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-xl p-3 w-52 z-50" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Alertas activas</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setVisibleAlertIds(new Set(ALERT_FILTER_OPTIONS.map(a => a.id)))}
                    className="text-[10px] text-primary hover:underline"
                  >
                    Todas
                  </button>
                  <span className="text-[10px] text-muted-foreground">·</span>
                  <button
                    onClick={() => setVisibleAlertIds(new Set())}
                    className="text-[10px] text-muted-foreground hover:text-foreground hover:underline"
                  >
                    Ninguna
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                {ALERT_FILTER_OPTIONS.map(alert => {
                  const isVisible = visibleAlertIds.has(alert.id);
                  return (
                    <button
                      key={alert.id}
                      onClick={() => toggleAlertPolygon(alert.id)}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-all duration-150 ${
                        isVisible ? 'bg-muted/40 text-foreground' : 'text-muted-foreground hover:bg-muted/20 hover:text-foreground'
                      }`}
                    >
                      <span
                        className="w-3 h-3 rounded-sm shrink-0 border"
                        style={{ background: isVisible ? alert.color + '55' : 'transparent', borderColor: alert.color }}
                      />
                      <span className="flex-1 text-left text-xs">{alert.name}</span>
                      <span
                        className={`w-2 h-2 rounded-full shrink-0 transition-opacity ${isVisible ? 'opacity-100' : 'opacity-0'}`}
                        style={{ background: alert.color }}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Selection count badge */}
        {selectedDotIds.size > 0 && (
          <div className="flex items-center gap-2 bg-indigo-500/20 border border-indigo-500/40 rounded-lg px-3 py-2 text-sm font-medium text-indigo-300 shadow-lg">
            <span className="w-2 h-2 rounded-full bg-indigo-400" />
            {selectedDotIds.size} seleccionado{selectedDotIds.size !== 1 ? 's' : ''}
            <button onClick={(e) => { e.stopPropagation(); setSelectedDotIds(new Set()); }} className="ml-1 text-indigo-400 hover:text-white transition-colors">
              <XMarkIcon className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Enviar Alerta button — shown when dots are selected */}
        {selectedDotIds.size > 0 && (
          <button
            onClick={handleOpenAlertPanel}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-3 py-2 text-sm font-semibold shadow-lg transition-all active:scale-95 border border-orange-400/30"
          >
            <BellAlertIcon className="w-3.5 h-3.5" />
            Enviar Alerta
          </button>
        )}

        {/* Right side controls */}
        <div className="ml-auto flex items-center gap-2">
          {/* Crear Alerta button */}
          <div className="relative">
            <button
              onClick={handleCrearAlerta}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium shadow-lg transition-all duration-150 border ${
                showDrawToolbar
                  ? 'bg-orange-500/20 text-orange-400 border-orange-500/40' :'bg-card/95 backdrop-blur-sm border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              <ShieldExclamationIcon className="w-3.5 h-3.5" />
              Crear Alerta
            </button>

            {/* Draw toolbar dropdown */}
            {showDrawToolbar && (
              <div className="absolute top-full right-0 mt-1 bg-card border border-border rounded-xl shadow-2xl p-3 w-56 z-50" onClick={e => e.stopPropagation()}>
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Forma de selección</p>
                <div className="flex gap-1.5 mb-3">
                  <button
                    onClick={() => setAlertAreaShape('polygon')}
                    className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-lg border text-xs font-medium transition-colors ${
                      alertAreaShape === 'polygon' ? 'bg-orange-500/15 border-orange-500/40 text-orange-400' : 'bg-muted border-border text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <polygon points="12,3 21,9 18,20 6,20 3,9" />
                    </svg>
                    Polígono libre
                  </button>
                  <button
                    onClick={() => setAlertAreaShape('circle')}
                    className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-lg border text-xs font-medium transition-colors ${
                      alertAreaShape === 'circle' ? 'bg-orange-500/15 border-orange-500/40 text-orange-400' : 'bg-muted border-border text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="12" r="9" />
                    </svg>
                    Círculo
                  </button>
                </div>
                <button
                  onClick={() => {
                    setIsDrawingArea(true);
                    setShowDrawToolbar(false);
                    showToast(
                      alertAreaShape === 'polygon' ?'Haz clic en el mapa para dibujar el polígono. Doble clic para cerrar.' :'Haz clic y arrastra para dibujar el círculo.',
                      'success'
                    );
                  }}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-lg py-2 text-sm font-semibold transition-all active:scale-95"
                >
                  Iniciar dibujo
                </button>
              </div>
            )}
          </div>

          {/* Map style toggle */}
          <button
            onClick={() => setMapStyle(s => s === 'dark' ? 'light' : 'dark')}
            className="flex items-center gap-1.5 bg-card/95 backdrop-blur-sm border border-border rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground shadow-lg transition-all duration-150"
          >
            {mapStyle === 'dark' ? <SunIcon className="w-3.5 h-3.5" /> : <MoonIcon className="w-3.5 h-3.5" />}
            {mapStyle === 'dark' ? 'Claro' : 'Oscuro'}
          </button>

          {/* Trajectory toggle */}
          <button
            onClick={() => setShowTrajectory(t => !t)}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium shadow-lg transition-all duration-150 border ${
              showTrajectory ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' : 'bg-card/95 backdrop-blur-sm border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 17H3a2 2 0 0 1-2-2V9l2-4h14l2 4v6a2 2 0 0 1-2 2h-2"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/></svg>
            Trayectoria
          </button>

          {/* Camera panel toggle */}
          <button
            onClick={() => setShowCameraPanel(p => !p)}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium shadow-lg transition-all duration-150 border ${
              showCameraPanel ? 'bg-primary/20 text-primary border-primary/40' : 'bg-card/95 backdrop-blur-sm border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            <CameraIcon className="w-3.5 h-3.5" />
            Cámaras
          </button>
        </div>
      </div>

      {/* Map — fills remaining height below toolbar */}
      <div className="flex-1 relative min-h-0">
        <MapView
          dots={filteredDots}
          selectedDotId={liveSelectedDot?.id ?? null}
          selectedDotIds={selectedDotIds}
          gridDotIds={gridDotIds}
          isAddingDot={isAddingDot}
          mapStyle={mapStyle}
          showTrajectory={showTrajectory}
          trajectoryRoutes={TRAJECTORY_ROUTES}
          timelineMs={timelineMs}
          visibleZoneIds={visibleZoneIds}
          dotAlerts={dotAlerts}
          isDrawingArea={isDrawingArea}
          drawShape={alertAreaShape}
          drawnPolygon={drawnPolygon}
          showAlertPolygons={visibleAlertIds.size > 0}
          visibleAlertIds={visibleAlertIds}
          onDotClick={handleDotClick}
          onDotRightClick={handleDotRightClick}
          onMultiRightClick={handleMultiRightClick}
          onMapClick={handleMapClick}
          onSelectionChange={handleSelectionChange}
          onAreaDrawn={handleAreaDrawn}
          onAcknowledgeAlert={acknowledgeAlert}
        />

        {/* Status legend */}
        <div
          className={`absolute bottom-4 left-4 z-[1001] rounded-lg p-3 shadow-lg${mapStyle !== 'light' ? ' bg-card/95 backdrop-blur-sm border border-border' : ''}`}
          style={mapStyle === 'light' ? {
            background: 'rgba(10,10,10,0.72)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255,255,255,0.12)',
          } : undefined}
        >
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: mapStyle === 'light' ? 'rgba(255,255,255,0.7)' : 'var(--color-muted-foreground, #9ca3af)' }}>Estado de puntos</p>
          <div className="flex flex-col gap-1.5">
            <button onClick={() => setFilterColor(prev => { const next = new Set(prev); next.has('alarma') ? next.delete('alarma') : next.add('alarma'); return next; })}
              className={`flex items-center justify-between gap-6 px-1.5 py-1 rounded-md transition-all duration-150 cursor-pointer w-full text-left ${filterColor.has('alarma') ? 'bg-red-500/20 ring-1 ring-red-500/50' : 'hover:bg-white/10'}`}>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0 animate-pulse" />
                <span className="text-xs font-semibold text-red-400">Alarma</span>
              </div>
              <span className="text-xs font-semibold text-red-400">{colorCounts.alarma}</span>
            </button>
            <button onClick={() => setFilterColor(prev => { const next = new Set(prev); next.has('streaming') ? next.delete('streaming') : next.add('streaming'); return next; })}
              className={`flex items-center justify-between gap-6 px-1.5 py-1 rounded-md transition-all duration-150 cursor-pointer w-full text-left ${filterColor.has('streaming') ? 'bg-green-500/20 ring-1 ring-green-500/50' : 'hover:bg-white/10'}`}>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-green-500 shrink-0" />
                <span className="text-xs font-semibold text-green-400">Streaming</span>
              </div>
              <span className="text-xs font-semibold text-green-400">{colorCounts.streaming}</span>
            </button>
            <button onClick={() => setFilterColor(prev => { const next = new Set(prev); next.has('blue') ? next.delete('blue') : next.add('blue'); return next; })}
              className={`flex items-center justify-between gap-6 px-1.5 py-1 rounded-md transition-all duration-150 cursor-pointer w-full text-left ${filterColor.has('blue') ? 'bg-blue-500/20 ring-1 ring-blue-500/50' : 'hover:bg-white/10'}`}>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-500 shrink-0" />
                <span className="text-xs" style={{ color: mapStyle === 'light' ? '#ffffff' : 'var(--color-foreground, #f9fafb)' }}>Activo</span>
              </div>
              <span className="text-xs font-semibold text-blue-500">{colorCounts.blue}</span>
            </button>
            <button onClick={() => setFilterColor(prev => { const next = new Set(prev); next.has('yellow') ? next.delete('yellow') : next.add('yellow'); return next; })}
              className={`flex items-center justify-between gap-6 px-1.5 py-1 rounded-md transition-all duration-150 cursor-pointer w-full text-left ${filterColor.has('yellow') ? 'bg-yellow-500/20 ring-1 ring-yellow-500/50' : 'hover:bg-white/10'}`}>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500 shrink-0" />
                <span className="text-xs" style={{ color: mapStyle === 'light' ? '#ffffff' : 'var(--color-foreground, #f9fafb)' }}>Desconectado</span>
              </div>
              <span className="text-xs font-semibold text-yellow-500">{colorCounts.yellow}</span>
            </button>
            <button onClick={() => setFilterColor(prev => { const next = new Set(prev); next.has('red') ? next.delete('red') : next.add('red'); return next; })}
              className={`flex items-center justify-between gap-6 px-1.5 py-1 rounded-md transition-all duration-150 cursor-pointer w-full text-left ${filterColor.has('red') ? 'bg-gray-500/20 ring-1 ring-gray-500/50' : 'hover:bg-white/10'}`}>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: '#000000', border: '1px solid #555' }} />
                <span className="text-xs" style={{ color: mapStyle === 'light' ? '#ffffff' : 'var(--color-foreground, #f9fafb)' }}>Fuera de línea</span>
              </div>
              <span className="text-xs font-semibold" style={{ color: '#6b7280' }}>{colorCounts.red}</span>
            </button>
            <button onClick={() => { setFilterColor(new Set()); setFilterCamera('all'); }}
              className={`border-t mt-1 pt-1.5 flex items-center justify-between w-full px-1.5 py-1 rounded-md transition-all duration-150 ${filterColor.size > 0 || filterCamera !== 'all' ? 'hover:bg-white/10 cursor-pointer' : 'cursor-default'}`}
              style={{ borderColor: mapStyle === 'light' ? 'rgba(255,255,255,0.15)' : undefined }}>
              <span className="text-xs" style={{ color: mapStyle === 'light' ? 'rgba(255,255,255,0.55)' : undefined }}>Total</span>
              <span className="text-xs font-semibold" style={{ color: mapStyle === 'light' ? '#ffffff' : undefined }}>{dots.length}</span>
            </button>
          </div>
          {filteredDots.length !== dots.length && (
            <p className="text-[10px] mt-1.5 border-t pt-1.5" style={{
              color: mapStyle === 'light' ? 'rgba(255,255,255,0.5)' : undefined,
              borderColor: mapStyle === 'light' ? 'rgba(255,255,255,0.15)' : undefined,
            }}>
              Mostrando {filteredDots.length} de {dots.length}
            </p>
          )}
        </div>

        {/* Trajectory legend */}
        {showTrajectory && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1001] bg-card/95 backdrop-blur-sm border border-emerald-500/30 rounded-lg px-4 py-2 shadow-lg pointer-events-none">
            <div className="flex items-center gap-4">
              {TRAJECTORY_ROUTES.map(r => (
                <div key={r.id} className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full border-2 border-white" style={{ background: r.color }} />
                  <div className="w-6 h-0.5 rounded" style={{ background: r.color }} />
                  <span className="text-xs text-foreground">{r.name.split(' ')[0]}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Timeline slider */}
        {showTrajectory && (
          <TimelineSlider
            progress={timelineProgress}
            isPlaying={isPlaying}
            timelineMs={timelineMs}
            minMs={timelineRange.minMs}
            maxMs={timelineRange.maxMs}
            onChange={setTimelineProgress}
            onPlayPause={() => {
              if (timelineProgress >= 100 && !isPlaying) setTimelineProgress(0);
              setIsPlaying(p => !p);
            }}
          />
        )}

        {/* Adding dot hint */}
        {isAddingDot && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1001] bg-yellow-500/90 backdrop-blur-sm text-black rounded-lg px-4 py-2 text-sm font-medium shadow-lg pointer-events-none">
            Haz clic en el mapa para colocar un nuevo punto
          </div>
        )}

        {/* Drawing area hint */}
        {isDrawingArea && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1001] bg-orange-500/90 backdrop-blur-sm text-white rounded-lg px-4 py-2 text-sm font-medium shadow-lg pointer-events-none">
            {alertAreaShape === 'polygon' ?'Haz clic para añadir vértices · Doble clic para cerrar el polígono' :'Haz clic y arrastra para dibujar el círculo'
            }
          </div>
        )}

        {/* Drag-select hint */}
        {!isAddingDot && !isDrawingArea && selectedDotIds.size === 0 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
            <p className="text-[10px] text-muted-foreground/50 text-center">
              Shift+arrastra para seleccionar varios • Ctrl+clic para selección individual
            </p>
          </div>
        )}

        {/* Camera Panel overlay */}
        {showCameraPanel && (
          <CameraPanel dots={dots} onClose={() => setShowCameraPanel(false)} onAssignDotToSlot={handleAssignDotToSlot} />
        )}

        {/* Draggable dots list */}
        {showCameraPanel && <DraggableDotsList dots={dots} />}

        {/* Enviar Alerta panel */}
        {showAlertPanel && (
          <EnviarAlertaPanel
            selectedDotIds={selectedDotIds}
            allDots={dots}
            onClose={() => { setShowAlertPanel(false); setDrawnPolygon(null); }}
            onAddFromMap={handleAddFromMapForAlert}
            alertKind={alertPanelKind}
            areaShape={alertAreaShape}
            drawnPolygon={drawnPolygon}
            onSelectionChange={(ids) => setSelectedDotIds(ids)}
          />
        )}
      </div>

      {/* Single dot context menu */}
      {contextMenu && (
        <div ref={contextMenuRef} className="fixed z-[1002] bg-card border border-border rounded-lg shadow-2xl py-1 w-48"
          style={{ left: contextMenu.x, top: contextMenu.y }} onMouseDown={e => e.stopPropagation()}>
          <div className="px-3 py-2 border-b border-border">
            <p className="text-xs font-semibold text-foreground">#{contextMenu.dot.dotId}</p>
            <p className="text-[11px] text-muted-foreground truncate">{contextMenu.dot.name}</p>
          </div>
          <button onClick={() => handleAssignCamera(contextMenu.dot, 'A')} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors">
            <CameraIcon className="w-3.5 h-3.5 text-muted-foreground" />Añadir Cámara A
          </button>
          <button onClick={() => handleAssignCamera(contextMenu.dot, 'B')} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors">
            <CameraIcon className="w-3.5 h-3.5 text-muted-foreground" />Añadir Cámara B
          </button>
          <button onClick={() => handleAssignCamera(contextMenu.dot, 'both')} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors">
            <CameraIcon className="w-3.5 h-3.5 text-blue-400" />Añadir Cámara A + B
          </button>
          <div className="border-t border-border my-1" />
          <button onClick={() => { addDotToGrid(contextMenu.dot, 'A'); setContextMenu(null); showToast(`#${contextMenu.dot.dotId} agregado al grid`); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-emerald-400 hover:bg-emerald-500/10 transition-colors">
            <CameraIcon className="w-3.5 h-3.5" />Agregar al grid
          </button>
          <div className="border-t border-border my-1" />
          <button onClick={() => {
            setSelectedDotIds(new Set([contextMenu.dot.id]));
            setContextMenu(null);
            setShowAlertPanel(true);
            setAlertPanelKind('notification');
          }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-orange-400 hover:bg-orange-500/10 transition-colors">
            <BellAlertIcon className="w-3.5 h-3.5" />Enviar Alerta
          </button>
          <div className="border-t border-border my-1" />
          <button onClick={() => handleEditOpen(contextMenu.dot)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors">
            <PencilSquareIcon className="w-3.5 h-3.5 text-muted-foreground" />Editar punto
          </button>
          <button onClick={() => handleDeleteOpen(contextMenu.dot)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors">
            <TrashIcon className="w-3.5 h-3.5" />Eliminar punto
          </button>
          <div className="border-t border-border my-1" />
          <button onClick={() => { setSelectedDotIds(new Set([contextMenu.dot.id])); setContextMenu(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-muted transition-colors">
            <XMarkIcon className="w-3.5 h-3.5" />Deseleccionar
          </button>
          <button onClick={() => { setSelectedDotIds(new Set()); setSelectedDot(null); setContextMenu(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-muted transition-colors">
            <XMarkIcon className="w-3.5 h-3.5" />Deseleccionar todos
          </button>
        </div>
      )}

      {/* Multi-selection context menu */}
      {multiContextMenu && selectedDotIds.size > 1 && (
        <div ref={multiContextMenuRef} className="fixed z-[1002] bg-card border border-border rounded-lg shadow-2xl py-1 w-52"
          style={{ left: multiContextMenu.x, top: multiContextMenu.y }} onMouseDown={e => e.stopPropagation()}>
          <div className="px-3 py-2 border-b border-border flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-indigo-400" />
            <p className="text-xs font-semibold text-foreground">{selectedDotIds.size} puntos seleccionados</p>
          </div>
          <button onClick={() => handleAssignCameraMulti('A')} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors">
            <CameraIcon className="w-3.5 h-3.5 text-muted-foreground" />Añadir Cámara A
          </button>
          <button onClick={() => handleAssignCameraMulti('B')} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors">
            <CameraIcon className="w-3.5 h-3.5 text-muted-foreground" />Añadir Cámara B
          </button>
          <button onClick={() => handleAssignCameraMulti('both')} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors">
            <CameraIcon className="w-3.5 h-3.5 text-blue-400" />Añadir Cámara A + B
          </button>
          <div className="border-t border-border my-1" />
          <button onClick={() => {
            dots.filter(d => selectedDotIds.has(d.id)).forEach(d => addDotToGrid(d, 'A'));
            setMultiContextMenu(null);
            setSelectedDotIds(new Set());
            showToast(`${selectedDotIds.size} puntos agregados al grid`);
          }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-emerald-400 hover:bg-emerald-500/10 transition-colors">
            <CameraIcon className="w-3.5 h-3.5" />Agregar al grid ({selectedDotIds.size})
          </button>
          <div className="border-t border-border my-1" />
          <button onClick={() => {
            setMultiContextMenu(null);
            setShowAlertPanel(true);
            setAlertPanelKind('notification');
          }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-orange-400 hover:bg-orange-500/10 transition-colors">
            <BellAlertIcon className="w-3.5 h-3.5" />Enviar Alerta ({selectedDotIds.size})
          </button>
          <div className="border-t border-border my-1" />
          <button onClick={() => { setSelectedDotIds(new Set()); setMultiContextMenu(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-muted transition-colors">
            <XMarkIcon className="w-3.5 h-3.5" />Deseleccionar todos
          </button>
        </div>
      )}

      {/* Detail panel */}
      {liveSelectedDot && selectedDotIds.size === 0 && (
        <DetailPanel
          dot={liveSelectedDot}
          dotAlert={dotAlerts[liveSelectedDot.dotId]}
          onClose={closeDetailPanel}
          onEdit={handleEditOpen}
          onDelete={handleDeleteOpen}
          onRefresh={handleRefreshDot}
          onAssignCamera={handleAssignCamera}
          onAcknowledge={() => acknowledgeAlert(liveSelectedDot.dotId)}
          onSendAlert={() => {
            setSelectedDotIds(new Set([liveSelectedDot.id]));
            setSelectedDot(null);
            setShowAlertPanel(true);
            setAlertPanelKind('notification');
          }}
        />
      )}

      {/* Add dot modal */}
      {showAddModal && (
        <Modal title="Añadir nuevo punto" onClose={() => { setShowAddModal(false); setAddName(''); setAddError(''); }}>
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Nombre de la persona</label>
              <input type="text" value={addName} onChange={e => setAddName(e.target.value)} placeholder="Ej. Carlos Mendoza"
                className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-ring transition-all" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Latitud</label>
                <input type="text" value={addLat} onChange={e => setAddLat(e.target.value)}
                  className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground font-mono outline-none focus:ring-1 focus:ring-ring" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Longitud</label>
                <input type="text" value={addLng} onChange={e => setAddLng(e.target.value)}
                  className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground font-mono outline-none focus:ring-1 focus:ring-ring" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Asignación de cámara</label>
              <select value={addCameras} onChange={e => setAddCameras(e.target.value as CameraStatus)}
                className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring">
                <option value="none">Sin cámara</option>
                <option value="A">Cámara A</option>
                <option value="B">Cámara B</option>
                <option value="both">Cámara A + B</option>
              </select>
            </div>
            {addError && <p className="text-xs text-red-400">{addError}</p>}
            <p className="text-[11px] text-muted-foreground">Se asignará automáticamente un ID de 4 dígitos único.</p>
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => { setShowAddModal(false); setAddName(''); setAddError(''); }}
                className="flex-1 border border-border rounded-lg py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all">Cancelar</button>
              <button type="button" onClick={handleAddDot}
                className="flex-1 bg-primary text-primary-foreground rounded-lg py-2 text-sm font-medium hover:bg-primary/90 transition-all active:scale-95">Añadir punto</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit dot modal */}
      {showEditModal && editingDot && (
        <Modal title={`Editar punto #${editingDot.dotId}`} onClose={() => { setShowEditModal(false); setEditingDot(null); }}>
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Nombre de la persona</label>
              <input type="text" value={editName} onChange={e => setEditName(e.target.value)}
                className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Asignación de cámara</label>
              <select value={editCameras} onChange={e => setEditCameras(e.target.value as CameraStatus)}
                className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring">
                <option value="none">Sin cámara</option>
                <option value="A">Cámara A</option>
                <option value="B">Cámara B</option>
                <option value="both">Cámara A + B</option>
              </select>
            </div>
            {editError && <p className="text-xs text-red-400">{editError}</p>}
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => { setShowEditModal(false); setEditingDot(null); }}
                className="flex-1 border border-border rounded-lg py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all">Cancelar</button>
              <button type="button" onClick={handleEditSave}
                className="flex-1 bg-primary text-primary-foreground rounded-lg py-2 text-sm font-medium hover:bg-primary/90 transition-all active:scale-95">Guardar cambios</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete confirm modal */}
      {showDeleteConfirm && deletingDot && (
        <Modal title="Eliminar punto" onClose={() => { setShowDeleteConfirm(false); setDeletingDot(null); }}>
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
              <ExclamationTriangleIcon className="w-4.5 h-4.5 text-red-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground">¿Eliminar #{deletingDot.dotId} — {deletingDot.name}?</p>
                <p className="text-xs text-muted-foreground mt-1">Esta acción no se puede deshacer.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setShowDeleteConfirm(false); setDeletingDot(null); }}
                className="flex-1 border border-border rounded-lg py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all">Cancelar</button>
              <button onClick={handleDeleteConfirm}
                className="flex-1 bg-red-500 text-white rounded-lg py-2 text-sm font-medium hover:opacity-90 transition-all active:scale-95">Sí, eliminar</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Toast */}
      {toast && (
        <div className={`absolute bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg px-4 py-3 shadow-2xl text-sm font-medium border ${
          toast.type === 'success' ? 'bg-card border-primary/30 text-foreground' : 'bg-red-500/10 border-red-500/30 text-red-400'
        }`}>
          {toast.type === 'success' ? <CheckCircleIcon className="w-4 h-4 text-primary shrink-0" /> : <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />}
          {toast.msg}
        </div>
      )}

      {/* Dot click popup */}
      {dotPopup && (
        <DotClickPopup
          dot={dotPopup.dot}
          x={dotPopup.x}
          y={dotPopup.y}
          onClose={() => setDotPopup(null)}
          trajectoryRoutes={TRAJECTORY_ROUTES}
          dotAlert={dotAlerts[dotPopup.dot.dotId]}
          onAddToGrid={handlePopupAddToGrid}
          onSendAlert={handlePopupSendAlert}
          onShowTrajectory={handlePopupShowTrajectory}
          onAcknowledgeAlert={() => { acknowledgeAlert(dotPopup.dot.dotId); setDotPopup(null); }}
          showToast={showToast}
          gridCells={gridCells}
          onRemoveFromGrid={(dotId) => {
            gridCells.forEach((cell, idx) => {
              if (cell && cell.dotId === dotId) removeDotFromGrid(idx);
            });
            showToast(`Cámaras de #${dotId} quitadas del grid`);
          }}
        />
      )}
    </div>
  );
}

// ─── Detail Panel ────────────────────────────────────────────────────────────
interface DetailPanelProps {
  dot: DotPerson;
  dotAlert?: { alertId: string; types: import('@/context/AlertContext').AlertType[]; acknowledged: boolean };
  onClose: () => void;
  onEdit: (dot: DotPerson) => void;
  onDelete: (dot: DotPerson) => void;
  onRefresh: (dot: DotPerson) => void;
  onAssignCamera: (dot: DotPerson, type: 'A' | 'B' | 'both') => void;
  onAcknowledge: () => void;
  onSendAlert: () => void;
}

function DetailPanel({ dot, dotAlert, onClose, onEdit, onDelete, onRefresh, onAssignCamera, onAcknowledge, onSendAlert }: DetailPanelProps) {
  const color = getEffectiveDotColor(dot);
  const colorConfig = {
    blue:      { ring: 'ring-blue-500/40',   bg: 'bg-blue-500/10',   text: 'text-blue-400' },
    yellow:    { ring: 'ring-yellow-500/40', bg: 'bg-yellow-500/10', text: 'text-yellow-400' },
    red:       { ring: 'ring-gray-500/40',   bg: 'bg-gray-500/10',   text: 'text-gray-400' },
    alarma:    { ring: 'ring-red-500/40',    bg: 'bg-red-500/10',    text: 'text-red-400' },
    streaming: { ring: 'ring-green-500/40',  bg: 'bg-green-500/10',  text: 'text-green-400' },
  }[color];

  return (
    <div className="absolute top-16 right-4 z-40 w-72 bg-card/98 backdrop-blur-md border border-border rounded-xl shadow-2xl" onClick={e => e.stopPropagation()}>
      {/* Active alert banner */}
      {dotAlert && (
        <div className={`px-4 py-2.5 border-b flex items-center gap-2 ${dotAlert.acknowledged ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-orange-500/10 border-orange-500/20'}`}>
          <BellAlertIcon className={`w-3.5 h-3.5 shrink-0 ${dotAlert.acknowledged ? 'text-emerald-400' : 'text-orange-400 animate-pulse'}`} />
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-semibold ${dotAlert.acknowledged ? 'text-emerald-400' : 'text-orange-400'}`}>
              {dotAlert.acknowledged ? 'Alerta confirmada' : 'Alerta activa'}
            </p>
            <p className="text-[10px] text-muted-foreground truncate">
              {dotAlert.types.join(', ')}
            </p>
          </div>
          {!dotAlert.acknowledged && (
            <button onClick={onAcknowledge}
              className="text-[10px] bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 rounded-md px-2 py-1 hover:bg-emerald-500/30 transition-colors shrink-0 font-semibold">
              ACK
            </button>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ring-2 ${colorConfig.ring} ${colorConfig.bg}`}>
            <span className={`text-sm font-bold ${colorConfig.text}`}>{dot.dotId.slice(0, 2)}</span>
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground leading-tight">{dot.name}</p>
            <p className="text-xs text-muted-foreground">#{dot.dotId}</p>
          </div>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted">
          <XMarkIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Status */}
      <div className="p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Estado de conexión</span>
          <StatusBadge color={color} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Cámara asignada</span>
          <CameraBadge cameras={dot.cameras} />
        </div>
        <div className="flex items-start justify-between gap-2">
          <span className="text-xs text-muted-foreground">Última actualización</span>
          <div className="text-right">
            <p className="text-xs font-medium text-foreground">{formatRelativeTime(dot.lastUpdate)}</p>
            <p className="text-[10px] font-mono text-muted-foreground">{formatTimestamp(dot.lastUpdate)}</p>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Coordenadas</span>
          <span className="text-[10px] font-mono text-muted-foreground">{dot.lat.toFixed(4)}, {dot.lng.toFixed(4)}</span>
        </div>
      </div>

      {/* Camera quick-assign */}
      <div className="px-4 pb-3 border-t border-border pt-3">
        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Asignar cámara</p>
        <div className="grid grid-cols-3 gap-1.5">
          {(['A', 'B', 'both'] as const).map((type) => (
            <button key={`assign-${type}`} onClick={() => onAssignCamera(dot, type)}
              className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg border text-xs font-medium transition-all duration-150 active:scale-95 ${
                (type === 'A' && (dot.cameras === 'A' || dot.cameras === 'both')) ||
                (type === 'B' && (dot.cameras === 'B' || dot.cameras === 'both')) ||
                (type === 'both' && dot.cameras === 'both')
                  ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-muted border-border text-muted-foreground hover:text-foreground hover:border-border/80'
              }`}>
              <CameraIcon className="w-3 h-3" />
              {type === 'both' ? 'A + B' : `Cam ${type}`}
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 pb-3 flex gap-2">
        <button onClick={() => onRefresh(dot)} className="flex items-center gap-1.5 flex-1 justify-center border border-border rounded-lg py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all active:scale-95">
          <ArrowPathIcon className="w-3 h-3" />Actualizar
        </button>
        <button onClick={() => onEdit(dot)} className="flex items-center gap-1.5 flex-1 justify-center bg-secondary border border-border rounded-lg py-2 text-xs font-medium text-foreground hover:bg-muted transition-all active:scale-95">
          <PencilSquareIcon className="w-3 h-3" />Editar
        </button>
        <button onClick={() => onDelete(dot)} className="flex items-center gap-1.5 justify-center border border-red-500/30 rounded-lg py-2 px-3 text-xs font-medium text-red-400 hover:bg-red-500/10 transition-all active:scale-95">
          <TrashIcon className="w-3 h-3" />
        </button>
      </div>

      {/* Send alert button */}
      <div className="px-4 pb-4">
        <button onClick={onSendAlert}
          className="w-full flex items-center justify-center gap-2 bg-orange-500/10 border border-orange-500/30 text-orange-400 rounded-lg py-2 text-xs font-semibold hover:bg-orange-500/20 transition-all active:scale-95">
          <BellAlertIcon className="w-3.5 h-3.5" />
          Enviar Alerta
        </button>
      </div>
    </div>
  );
}

// ─── Timeline Slider ────────────────────────────────────────────────────────
interface TimelineSliderProps {
  progress: number;
  isPlaying: boolean;
  timelineMs: number;
  minMs: number;
  maxMs: number;
  onChange: (v: number) => void;
  onPlayPause: () => void;
}

function TimelineSlider({ progress, isPlaying, timelineMs, minMs, maxMs, onChange, onPlayPause }: TimelineSliderProps) {
  const formatTime = (ms: number) => {
    const d = new Date(ms);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const totalMinutes = Math.round((maxMs - minMs) / 60000);

  return (
    <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-[1001] w-[min(680px,90vw)] pointer-events-auto">
      <div className="bg-card/95 backdrop-blur-md border border-border rounded-xl shadow-2xl px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <svg className="w-3.5 h-3.5 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <span className="text-xs font-semibold text-foreground">Línea de tiempo</span>
            <span className="text-[10px] text-muted-foreground">· {totalMinutes} min de recorrido</span>
          </div>
          <span className="text-xs font-mono font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-md px-2 py-0.5">{formatTime(timelineMs)}</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={onPlayPause} className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/30 transition-all flex items-center justify-center">
            {isPlaying ? <PauseIcon className="w-3.5 h-3.5" /> : <PlayIcon className="w-3.5 h-3.5 ml-0.5" />}
          </button>
          <span className="text-[10px] font-mono text-muted-foreground flex-shrink-0">{formatTime(minMs)}</span>
          <div className="relative flex-1 h-5 flex items-center group">
            <div className="absolute inset-x-0 h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-full transition-none" style={{ width: `${progress}%` }} />
            </div>
            {TRAJECTORY_ROUTES.map(route => {
              const routeMin = Math.min(...route.points.map(p => new Date(p.timestamp).getTime()));
              const routeMax = Math.max(...route.points.map(p => new Date(p.timestamp).getTime()));
              const startPct = ((routeMin - minMs) / (maxMs - minMs)) * 100;
              const endPct = ((routeMax - minMs) / (maxMs - minMs)) * 100;
              return (
                <div key={route.id} className="absolute h-1.5 rounded-full opacity-60"
                  style={{ left: `${startPct}%`, width: `${endPct - startPct}%`, background: route.color, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              );
            })}
            <input type="range" min={0} max={100} step={0.1} value={progress} onChange={e => onChange(parseFloat(e.target.value))}
              className="absolute inset-0 w-full opacity-0 cursor-pointer h-full" style={{ zIndex: 2 }} />
            <div className="absolute w-4 h-4 rounded-full bg-white border-2 border-emerald-400 shadow-lg shadow-emerald-500/30 pointer-events-none transition-none"
              style={{ left: `calc(${progress}% - 8px)`, top: '50%', transform: 'translateY(-50%)', zIndex: 1 }} />
          </div>
          <span className="text-[10px] font-mono text-muted-foreground flex-shrink-0">{formatTime(maxMs)}</span>
        </div>
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          {TRAJECTORY_ROUTES.map(route => {
            const routeMax = Math.max(...route.points.map(p => new Date(p.timestamp).getTime()));
            const isActive = timelineMs >= Math.min(...route.points.map(p => new Date(p.timestamp).getTime()));
            const isDone = timelineMs >= routeMax;
            return (
              <div key={route.id} className="flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full border border-white/50 transition-opacity ${isActive ? 'opacity-100' : 'opacity-30'}`} style={{ background: route.color }} />
                <span className={`text-[10px] transition-opacity ${isActive ? 'text-foreground' : 'text-muted-foreground/40'}`}>
                  {route.name.split(' ')[0]}{isDone && <span className="ml-1 text-muted-foreground">✓</span>}
                </span>
              </div>
            );
          })}
          <span className="ml-auto text-[10px] text-muted-foreground/60">Arrastra para navegar en el tiempo</span>
        </div>
      </div>
    </div>
  );
}

// ─── Draggable Dots List ────────────────────────────────────────────────────
function DraggableDotsList({ dots }: { dots: DotPerson[] }) {
  const [collapsed, setCollapsed] = useState(false);
  const unassigned = dots.filter(d => d.cameras === 'none');
  const assigned = dots.filter(d => d.cameras !== 'none');

  const handleDragStart = (e: React.DragEvent, dot: DotPerson) => {
    e.dataTransfer.setData('text/plain', dot.dotId);
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div className="absolute top-4 left-4 z-[1001] bg-card/95 backdrop-blur-sm border border-border rounded-lg shadow-xl w-52 max-h-72 flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border cursor-pointer select-none" onClick={() => setCollapsed(c => !c)}>
        <div className="flex items-center gap-1.5">
          <CameraIcon className="w-3.5 h-3.5 text-primary" />
          <span className="text-[11px] font-semibold text-foreground">Arrastrar puntos</span>
        </div>
        <span className="text-[10px] text-muted-foreground">{collapsed ? '▼' : '▲'}</span>
      </div>
      {!collapsed && (
        <div className="overflow-y-auto flex-1 px-2 py-1.5 space-y-1">
          {unassigned.length > 0 && (
            <>
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground/60 px-1 pt-0.5">Sin cámara</p>
              {unassigned.map(dot => {
                const c = getEffectiveDotColor(dot);
                const cc = c === 'blue' ? 'bg-blue-500' : c === 'yellow' ? 'bg-yellow-500' : c === 'alarma' ? 'bg-red-500' : c === 'streaming' ? 'bg-green-500' : 'bg-black';
                return (
                  <div key={dot.id} draggable onDragStart={e => handleDragStart(e, dot)}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/30 hover:bg-muted/60 cursor-grab active:cursor-grabbing border border-border/40 transition-colors">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${cc}`} />
                    <span className="text-[10px] font-mono text-foreground/80">#{dot.dotId}</span>
                    <span className="text-[10px] text-muted-foreground truncate">{dot.name.split(' ')[0]}</span>
                  </div>
                );
              })}
            </>
          )}
          {assigned.length > 0 && (
            <>
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground/60 px-1 pt-1">Con cámara</p>
              {assigned.map(dot => {
                const c = getEffectiveDotColor(dot);
                const cc = c === 'blue' ? 'bg-blue-500' : c === 'yellow' ? 'bg-yellow-500' : c === 'alarma' ? 'bg-red-500' : c === 'streaming' ? 'bg-green-500' : 'bg-black';
                return (
                  <div key={dot.id} draggable onDragStart={e => handleDragStart(e, dot)}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/20 hover:bg-muted/50 cursor-grab active:cursor-grabbing border border-border/30 transition-colors opacity-70">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${cc}`} />
                    <span className="text-[10px] font-mono text-foreground/70">#{dot.dotId}</span>
                    <span className="text-[10px] text-muted-foreground truncate">{dot.name.split(' ')[0]}</span>
                    <CameraIcon className="w-2.5 h-2.5 text-primary/60 ml-auto shrink-0" />
                  </div>
                );
              })}
            </>
          )}
          {dots.length === 0 && <p className="text-[10px] text-muted-foreground text-center py-2">No hay puntos</p>}
        </div>
      )}
    </div>
  );
}

// ─── Dot Click Popup ────────────────────────────────────────────────────────
interface DotClickPopupProps {
  dot: DotPerson;
  x: number;
  y: number;
  onClose: () => void;
  trajectoryRoutes: import('@/data/dotsData').TrajectoryRoute[];
  dotAlert?: { alertId: string; types: import('@/context/AlertContext').AlertType[]; acknowledged: boolean; timestamp?: string };
  onAddToGrid: (dot: DotPerson, type: 'A' | 'B') => void;
  onSendAlert: (dot: DotPerson) => void;
  onShowTrajectory: () => void;
  onAcknowledgeAlert: () => void;
  showToast: (msg: string, type?: 'success' | 'error') => void;
  gridCells: Array<import('@/context/GridContext').GridCamera | null>;
  onRemoveFromGrid: (dotId: string) => void;
}

function DotClickPopup({ dot, x, y, onClose, trajectoryRoutes, dotAlert, onAddToGrid, onSendAlert, onShowTrajectory, onAcknowledgeAlert, showToast, gridCells, onRemoveFromGrid }: DotClickPopupProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: x + 12, top: y - 20 });

  useEffect(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = x + 12;
    let top = y - 20;
    if (left + rect.width > vw - 16) left = x - rect.width - 12;
    if (top + rect.height > vh - 16) top = vh - rect.height - 16;
    if (top < 8) top = 8;
    if (left < 8) left = 8;
    setPosition({ left, top });
  }, [x, y]);

  const color = getEffectiveDotColor(dot);
  const colorMap: Record<string, { dot: string; text: string; bg: string; border: string }> = {
    blue:      { dot: '#3b82f6', text: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/30' },
    yellow:    { dot: '#f59e0b', text: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
    red:       { dot: '#6b7280', text: 'text-gray-400',   bg: 'bg-gray-500/10',   border: 'border-gray-500/30' },
    alarma:    { dot: '#ef4444', text: 'text-red-400',    bg: 'bg-red-500/10',    border: 'border-red-500/30' },
    streaming: { dot: '#22c55e', text: 'text-green-400',  bg: 'bg-green-500/10',  border: 'border-green-500/30' },
  };
  const cc = colorMap[color] ?? colorMap.blue;

  // Determine if this dot is streaming (in camera grid or either camera in Vista Cámaras grid)
  const isInGrid = gridCells.some(cell => cell !== null && cell.dotId === dot.dotId);
  const isStreaming = isInGrid;

  // Compute last online
  const diffMs = Date.now() - new Date(dot.lastUpdate).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  let lastOnline: string;
  if (diffMin < 1) lastOnline = 'Hace menos de 1 min';
  else if (diffMin < 60) lastOnline = `Hace ${diffMin} min`;
  else {
    const hrs = Math.floor(diffMin / 60);
    const mins = diffMin % 60;
    lastOnline = mins > 0 ? `Hace ${hrs}h ${mins}min` : `Hace ${hrs}h`;
  }

  // Compute login time today (simulated: lastUpdate or a fixed offset)
  const loginDate = new Date(dot.lastUpdate);
  const loginHour = loginDate.getHours();
  const loginMin = loginDate.getMinutes();
  // Simulate login ~2-4 hours before lastUpdate
  const loginSimMs = new Date(dot.lastUpdate).getTime() - (2 * 60 + 30) * 60 * 1000;
  const loginSim = new Date(loginSimMs);
  const pad = (n: number) => String(n).padStart(2, '0');
  const loginTimeStr = `${pad(loginSim.getHours())}:${pad(loginSim.getMinutes())}`;
  const lastOnlineTimeStr = `${pad(loginDate.getHours())}:${pad(loginDate.getMinutes())}`;

  // Badge (camera assignment)
  const badgeLabel = dot.cameras === 'both' ? 'Cám A + B' : dot.cameras === 'A' ? 'Cám A' : dot.cameras === 'B' ? 'Cám B' : 'Sin cámara';
  const badgeColor = dot.cameras !== 'none' ? 'text-blue-400 bg-blue-500/10 border-blue-500/30' : 'text-gray-400 bg-gray-500/10 border-gray-500/30';

  // Trajectory for this dot
  const route = trajectoryRoutes.find(r => r.dotId === dot.dotId);

  // Event timeline (simulated based on dot data)
  const events = [
    { time: loginTimeStr, label: 'Inicio de turno', icon: '🟢' },
    ...(route ? [{ time: `${pad(new Date(route.points[0].timestamp).getHours())}:${pad(new Date(route.points[0].timestamp).getMinutes())}`, label: 'Inicio de trayectoria', icon: '🚗' }] : []),
    ...(dot.dotType === 'alarma' ? [{ time: lastOnlineTimeStr, label: 'Alarma activada', icon: '🔴' }] : []),
    ...(dot.dotType === 'streaming' ? [{ time: lastOnlineTimeStr, label: 'Streaming iniciado', icon: '📡' }] : []),
    { time: lastOnlineTimeStr, label: 'Última actualización', icon: '📍' },
  ];

  return (
    <div
      ref={containerRef}
      className="fixed z-[2000] w-72 bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
      style={{ left: position.left, top: position.top }}
      onClick={e => e.stopPropagation()}
    >
      {/* Alarm info banner — shown on top for alarma dots */}
      {dot.dotType === 'alarma' && dot.alarmReport && (() => {
        const alarmDate = new Date(dot.lastUpdate);
        const alarmPad = (n: number) => String(n).padStart(2, '0');
        const alarmTimeStr = `${alarmPad(alarmDate.getDate())}/${alarmPad(alarmDate.getMonth() + 1)} ${alarmPad(alarmDate.getHours())}:${alarmPad(alarmDate.getMinutes())}`;
        const alarmDiffMs = Date.now() - alarmDate.getTime();
        const alarmDiffMin = Math.floor(alarmDiffMs / 60000);
        let alarmAgoStr: string;
        if (alarmDiffMin < 1) alarmAgoStr = 'Hace menos de 1 min';
        else if (alarmDiffMin < 60) alarmAgoStr = `Hace ${alarmDiffMin} min`;
        else {
          const hrs = Math.floor(alarmDiffMin / 60);
          const mins = alarmDiffMin % 60;
          alarmAgoStr = mins > 0 ? `Hace ${hrs}h ${mins}min` : `Hace ${hrs}h`;
        }
        return (
          <div className="bg-red-950/80 border-b border-red-500/40 px-4 py-3">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
              <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider">🔔 Alarma Activa</span>
            </div>
            <p className="text-[10px] text-red-200/80 leading-relaxed mb-1.5">{dot.alarmReport}</p>
            <div className="flex items-center gap-1.5 text-[9px] text-red-400/70">
              <span>🕐</span>
              <span>Estado activado: {alarmTimeStr} · {alarmAgoStr}</span>
            </div>
          </div>
        );
      })()}

      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 border-b border-border ${cc.bg}`}>
        <div className="flex items-center gap-2.5">
          <div className="relative">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: cc.dot + '22', border: `2px solid ${cc.dot}55` }}>
              <span className="text-xs font-bold" style={{ color: cc.dot }}>{dot.dotId.slice(0, 2)}</span>
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-card" style={{ background: cc.dot }} />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground leading-tight">{dot.name}</p>
            <p className="text-[10px] text-muted-foreground">ID #{dot.dotId}</p>
          </div>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted/60">
          <XMarkIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
        {/* Info grid */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-muted/30 rounded-lg p-2.5">
            <p className="text-[9px] uppercase tracking-wider text-muted-foreground mb-1">Badge / Cámara</p>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${badgeColor}`}>{badgeLabel}</span>
          </div>
          <div className="bg-muted/30 rounded-lg p-2.5">
            <p className="text-[9px] uppercase tracking-wider text-muted-foreground mb-1">Estado</p>
            <div className="flex items-center gap-1.5">
              {isStreaming ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-[10px] font-semibold text-green-400">Streaming</span>
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full" style={{ background: cc.dot }} />
                  <span className={`text-[10px] font-semibold ${cc.text}`}>{getColorLabel(color)}</span>
                </>
              )}
            </div>
          </div>
          <div className="bg-muted/30 rounded-lg p-2.5">
            <p className="text-[9px] uppercase tracking-wider text-muted-foreground mb-1">Último en línea</p>
            <p className="text-[10px] font-medium text-foreground">{lastOnline}</p>
            <p className="text-[9px] font-mono text-muted-foreground">{lastOnlineTimeStr}</p>
          </div>
          <div className="bg-muted/30 rounded-lg p-2.5">
            <p className="text-[9px] uppercase tracking-wider text-muted-foreground mb-1">Login hoy</p>
            <p className="text-[10px] font-medium text-foreground">{loginTimeStr}</p>
            <p className="text-[9px] text-muted-foreground">Inicio de turno</p>
          </div>
        </div>

        {/* Alarm report */}
        {dot.dotType === 'alarma' && dot.alarmReport && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            <p className="text-[9px] uppercase tracking-wider text-red-400 mb-1.5 font-semibold">⚠ Reporte de Alarma</p>
            <p className="text-[10px] text-red-200/80 leading-relaxed">{dot.alarmReport}</p>
          </div>
        )}

        {/* Trajectory */}
        <div>
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground mb-2 font-semibold">Trayectoria</p>
          {route ? (
            <div className="bg-muted/30 rounded-lg p-2.5 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground">Ruta activa</span>
                <span className="text-[10px] font-semibold" style={{ color: route.color }}>{route.name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground">Puntos registrados</span>
                <span className="text-[10px] font-mono text-foreground">{route.points.length} pts</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground">Velocidad actual</span>
                <span className="text-[10px] font-mono text-foreground">{route.points[route.points.length - 1]?.speed ?? 0} km/h</span>
              </div>
              {/* Mini route visualization */}
              <div className="mt-1.5 h-1 rounded-full overflow-hidden bg-muted">
                <div className="h-full rounded-full" style={{ background: `linear-gradient(to right, ${route.color}88, ${route.color})`, width: '100%' }} />
              </div>
            </div>
          ) : (
            <div className="bg-muted/20 rounded-lg p-2.5 text-center">
              <p className="text-[10px] text-muted-foreground">Sin trayectoria registrada</p>
            </div>
          )}
        </div>

        {/* Event timeline */}
        <div>
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground mb-2 font-semibold">Línea de eventos</p>
          <div className="relative pl-4">
            <div className="absolute left-1.5 top-0 bottom-0 w-px bg-border" />
            {events.map((ev, i) => (
              <div key={i} className="relative flex items-start gap-2 mb-2 last:mb-0">
                <div className="absolute -left-[11px] w-2 h-2 rounded-full bg-card border border-border mt-0.5" />
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[9px] font-mono text-muted-foreground w-10 shrink-0">{ev.time}</span>
                  <span className="text-[9px]">{ev.icon}</span>
                  <span className="text-[10px] text-foreground/80 truncate">{ev.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Camera buttons */}
        <div>
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground mb-2 font-semibold">Cámaras</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600/20 border border-blue-500/40 text-blue-400 hover:bg-blue-600/30 hover:border-blue-500/60 transition-all text-[10px] font-semibold active:scale-95"
              onClick={() => { onAddToGrid(dot, 'A'); onClose(); }}
            >
              <span>📷</span>
              <span>Ver Cámara A</span>
            </button>
            <button
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-purple-600/20 border border-purple-500/40 text-purple-400 hover:bg-purple-600/30 hover:border-purple-500/60 transition-all text-[10px] font-semibold active:scale-95"
              onClick={() => { onAddToGrid(dot, 'B'); onClose(); }}
            >
              <span>📹</span>
              <span>Ver Cámara B</span>
            </button>
          </div>
        </div>

        {/* Streaming status banner */}
        {isStreaming && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2.5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
              <span className="text-[10px] font-semibold text-green-400">📡 Transmitiendo en grid</span>
            </div>
            <button
              className="flex items-center gap-1 px-2 py-1 rounded-md bg-red-600/20 border border-red-500/40 text-red-400 hover:bg-red-600/30 transition-all text-[9px] font-semibold active:scale-95 shrink-0"
              onClick={() => { onRemoveFromGrid(dot.dotId); onClose(); }}
            >
              <span>✕</span>
              <span>Quitar del grid</span>
            </button>
          </div>
        )}

        {/* Quick actions */}
        <div>
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground mb-2 font-semibold">Acciones rápidas</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-muted/40 border border-border hover:bg-muted/70 hover:border-border/80 transition-all text-[10px] font-medium text-foreground/80 active:scale-95"
              onClick={() => { showToast(`Mensaje enviado a ${dot.name}`); onClose(); }}
            >
              <span>💬</span>
              <span>Mensaje</span>
            </button>
            <button
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-muted/40 border border-border hover:bg-muted/70 hover:border-border/80 transition-all text-[10px] font-medium text-foreground/80 active:scale-95"
              onClick={() => { onShowTrajectory(); }}
            >
              <span>🗺️</span>
              <span>Trayectoria</span>
            </button>
            <button
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-muted/40 border border-border hover:bg-muted/70 hover:border-border/80 transition-all text-[10px] font-medium text-foreground/80 active:scale-95"
              onClick={() => { window.location.href = '/audit-log'; }}
            >
              <span>📋</span>
              <span>Historial</span>
            </button>
            <button
              className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border transition-all text-[10px] font-medium active:scale-95 ${dot.dotType === 'alarma' ? 'bg-red-600/20 border-red-500/40 text-red-400 hover:bg-red-600/30' : 'bg-orange-600/20 border-orange-500/40 text-orange-400 hover:bg-orange-600/30'}`}
              onClick={() => {
                if (dot.dotType === 'alarma') {
                  onAcknowledgeAlert();
                } else {
                  onSendAlert(dot);
                }
              }}
            >
              <span>{dot.dotType === 'alarma' ? '🔕' : '🔔'}</span>
              <span>{dot.dotType === 'alarma' ? 'Atender' : 'Alerta'}</span>
            </button>
            <button
              className="col-span-2 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-teal-600/20 border border-teal-500/40 text-teal-400 hover:bg-teal-600/30 hover:border-teal-500/60 transition-all text-[10px] font-semibold active:scale-95"
              onClick={() => { onAddToGrid(dot, 'A'); onAddToGrid(dot, 'B'); onClose(); }}
            >
              <span>🎥</span>
              <span>Ver las dos cámaras</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Modal wrapper ─────────────────────────────────────────────────────────
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted">
            <XMarkIcon className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}